import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { Avatar, Box, ClickAwayListener, Grid, Paper, Popper, useMediaQuery } from '@mui/material';
import MainCard from '../../../berry/components/cards/MainCard';
import Transitions from '../../../berry/components/extended/Transitions';
import NotificationList from './NotificationList';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { IconBell, IconBellExclamation } from '@tabler/icons-react';
import { useNotificationList } from 'controller/CommonService';
import * as React from 'react';
import { AppContextType, AppContext } from 'context/AppContext';
import io from 'socket.io-client';
import { ResolveSingleNotification } from 'services/resolvers/Common';
import { debounce } from 'utils/collections';
// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
    const { appToken } = React.useContext(AppContext) as AppContextType;
    const { notificationList, addNotification } = useNotificationList();
    const theme = useTheme();
    const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
    const [open, setOpen] = useState(false);
    const [newNotification, setNewNotification] = useState(false);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const anchorRef = useRef<any>(null);

    const debouncedAddNotification = React.useMemo(() => debounce(addNotification, 1000), [addNotification]);

    const handleToggle = () => {
        setNewNotification(false);
        setOpen((prevOpen) => !prevOpen);
    };

    const handleClose = (event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }
        prevOpen.current = open;
    }, [open]);

    const memoSocket = React.useMemo(
        () =>
            io(`${process.env.REACT_APP_WEBSOCKET_URL || 'https://socketsdev.teamstation.dev'}`, {
                auth: { Authorization: `${appToken}` },
                extraHeaders: { Authorization: `${appToken}` },
                reconnection: true,
                reconnectionDelay: 5000,
                reconnectionAttempts: 100
            } as any),
        [appToken]
    );

    React.useEffect(() => {
        memoSocket.on('message', (newNotif: any) => {
            const notificationToEnqueue = ResolveSingleNotification('hyrerocket', newNotif);
            if (notificationToEnqueue) {
                !open && setNewNotification(true);
                debouncedAddNotification(notificationToEnqueue);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memoSocket]);

    return (
        <>
            <Box
                sx={{
                    ml: 2,
                    mr: 3,
                    [theme.breakpoints.down('md')]: {
                        mr: 2
                    }
                }}
            >
                <Avatar
                    variant="rounded"
                    sx={{
                        ...theme.typography.commonAvatar,
                        ...theme.typography.mediumAvatar,
                        transition: 'all .2s ease-in-out',
                        background: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.secondary.light,
                        color: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                        '&[aria-controls="menu-list-grow"],&:hover': {
                            background: theme.palette.mode === 'dark' ? theme.palette.warning.dark : theme.palette.secondary.dark,
                            color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.secondary.light
                        },
                        ...(newNotification && {
                            background: theme.palette.warning.dark,
                            color: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.secondary.light
                        })
                    }}
                    ref={anchorRef}
                    aria-controls={open ? 'menu-list-grow' : undefined}
                    aria-haspopup="true"
                    onClick={handleToggle}
                    color="inherit"
                >
                    {newNotification ? <IconBellExclamation stroke={1.5} size="1.3rem" /> : <IconBell stroke={1.5} size="1.3rem" />}
                </Avatar>
            </Box>

            <Popper
                placement={matchesXs ? 'bottom' : 'bottom-end'}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [matchesXs ? 5 : 0, 20]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Grid container direction="column" spacing={2}>
                                            <Grid item xs={12}>
                                                <PerfectScrollbar
                                                    style={{ height: '100%', maxHeight: 'calc(100vh - 205px)', overflowX: 'hidden' }}
                                                >
                                                    <NotificationList notificationList={notificationList} handleClose={handleToggle} />
                                                </PerfectScrollbar>
                                            </Grid>
                                        </Grid>
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default NotificationSection;
